/* global NotificationPermission */
import { Base } from "ui/Scripts/source/framework/base";
import { Session } from "../session/session";
import * as store from "../../framework/customStore";
import * as storeActions from "../store/storeActions";
import * as baseService from "../services/baseService";
import * as userService from "../services/userService";
import { IndexedDbManager } from "../indexedDb/indexedDbManager";

export class NotificationManager {
    private static serviceWorkerIsAvailable(): boolean {
        return ("serviceWorker" in navigator);
    }

    //private static displayNotification() {
    //    if (!NotificationManager.serviceWorkerIsAvailable()) return;
    //    const options: NotificationOptions = {
    //        body: "This is the notification body test!",
    //        icon: appConfig.rootUrl + "Content/logo_096x096.png",
    //        badge: appConfig.rootUrl + "Content/logo_096x096.png",
    //        lang: appConfig.culture,
    //    }
    //    navigator.serviceWorker.ready.then((serviceWorkerRegistration: ServiceWorkerRegistration) => {
    //        serviceWorkerRegistration.showNotification("Notifications are now in use from SW!", options);
    //    });
    //}

    static removePushSubscriptions(): Promise<boolean> {
        const userCode = Session.getAuthenticatedUserCodeLocalStorage();
        if (Base.isNullOrEmpty(userCode) || Base.isNullOrEmpty(Session.getAccessToken())) return new Promise<boolean>((resolve) => { resolve(true); });
        if (!NotificationManager.serviceWorkerIsAvailable()) return new Promise<boolean>((resolve) => { resolve(true); });
        return navigator.serviceWorker.ready
            .then((swRegistration: ServiceWorkerRegistration) => {
                return swRegistration.pushManager.getSubscription();
            })
            .then((oldSubscription: PushSubscription) => {
                //console.log(oldSubscription);
                if (Base.isNullOrUndefined(oldSubscription)) {
                    return new Promise<boolean>((resolve) => { resolve(true); });
                }
                //Unsubscribe subscription
                return oldSubscription.unsubscribe();
            })
            .then(() => {
                //Remove user subscriptions from browser
                console.log("[App] Remove user subscriptions from browser", userCode);
                return IndexedDbManager.removeUserPushSubscriptions(userCode);
            })
            .then((removedSubscriptionIds: string[]) => {
                //Remove user subscriptions from server
                console.log("[App] Remove old subscriptions from server", removedSubscriptionIds);
                if (removedSubscriptionIds.length < 1) return new Promise<boolean>((resolve) => { resolve(true); });
                return userService.removePushSubscriptions(removedSubscriptionIds)
                    .then(success => {
                        console.log("[App] Successfully removed expired PushSubscriptions from server", removedSubscriptionIds, success);
                        return new Promise<boolean>((resolve) => { resolve(true); });
                    })
                    .catch(error => {
                        const errorMessage = baseService.getErrorMessageFromError(error);
                        store.customStore.dispatch(storeActions.showErrorMessage(errorMessage));
                        return Promise.reject(new TypeError("[App] Failed to remove PushSubscriptions from server! Error: " + errorMessage));
                    });
            })
            .catch(error => {
                console.log("[App] Failed to unsubscribe PushSubscriptions! Error: ", error);
                return new Promise<boolean>((resolve) => { resolve(false); });
            });
    }

    private static createPushSubscription(): Promise<void> {
        const userCode = Session.getAuthenticatedUserCodeLocalStorage();
        if (Base.isNullOrEmpty(userCode) || Base.isNullOrEmpty(Session.getAccessToken())) return new Promise<void>((resolve) => { resolve(); });
        if (!NotificationManager.serviceWorkerIsAvailable()) return new Promise<void>((resolve) => { resolve(); });
        let serviceWorkerRegistration: ServiceWorkerRegistration = null;
        return navigator.serviceWorker.ready
            .then((swRegistration: ServiceWorkerRegistration) => {
                serviceWorkerRegistration = swRegistration;
                return swRegistration.pushManager.getSubscription();
            })
            .then((oldSubscription: PushSubscription) => {
                if (Base.isNullOrUndefined(oldSubscription)) {
                    //Create new subscription
                    return serviceWorkerRegistration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: Base.urlBase64ToUint8Array(appConfig.vapidPublicKey)
                    });
                } else {
                    //Subscription exists
                    return Promise.reject(new TypeError("[App] PushSubscription already exists"));
                }
            })
            .then((newSubscription: PushSubscription) => {
                //Store new subscription to server
                console.log("[App] Store new PushSubscription to server");
                return userService.savePushSubscription(newSubscription)
                    .then(success => {
                        console.log("[App] Successfully saved PushSubscription to server", success);
                        return new Promise<string>((resolve) => { resolve(success.id); });
                    })
                    .catch(error => {
                        const errorMessage = baseService.getErrorMessageFromError(error);
                        store.customStore.dispatch(storeActions.showErrorMessage(baseService.getErrorMessageFromError(error)));
                        return Promise.reject(new TypeError("[App] Failed to save PushSubscription to server! Error: " + errorMessage));
                    });
            })
            .then((newSubscriptionId: string) => {
                //Store new subscription to browser
                console.log("[App] Store new PushSubscription to browser", newSubscriptionId);
                return IndexedDbManager.storePushSubscription(newSubscriptionId, userCode).then(() => {
                    return new Promise<string>((resolve) => { resolve(newSubscriptionId); });
                });
            })
            .then((newSubscriptionId: string) => {
                //Remove old subscriptions from browser
                console.log("[App] Remove old subscriptions from browser");
                return IndexedDbManager.getPushSubscriptionIds(userCode).then((subscriptionIds: string[]) => {
                    const removedSubscriptionIds: string[] = subscriptionIds.filter(i => i !== newSubscriptionId);
                    if (removedSubscriptionIds.length < 1) return new Promise<string[]>((resolve) => { resolve([]); });
                    return IndexedDbManager.removePushSubscriptions(removedSubscriptionIds).then(() => {
                        return new Promise<string[]>((resolve) => { resolve(removedSubscriptionIds); });
                    });
                });
            })
            .then((removedSubscriptionIds: string[]) => {
                //Remove old subscriptions from server
                console.log("[App] Remove old subscriptions from server", removedSubscriptionIds);
                if (removedSubscriptionIds.length < 1) return new Promise<void>((resolve) => { resolve(); });
                return userService.removePushSubscriptions(removedSubscriptionIds)
                    .then(success => {
                        console.log("[App] Successfully removed expired PushSubscriptions from server", removedSubscriptionIds, success);
                        return new Promise<void>((resolve) => { resolve(); });
                    })
                    .catch(error => {
                        const errorMessage = baseService.getErrorMessageFromError(error);
                        store.customStore.dispatch(storeActions.showErrorMessage(errorMessage));
                        return Promise.reject(new TypeError("[App] Failed to remove PushSubscriptions from server! Error: " + errorMessage));
                    });
            })
            .catch(error => {
                console.log(error);
            });
    }

    private static requestPermission(requesPermissionOnDefault: boolean = true) {
        //Promise is used in modern browsers, callback is used in some old browsers
        try {
            Notification.requestPermission().then((permission: NotificationPermission) => NotificationManager.handleNotificationPermissionRequest(permission, requesPermissionOnDefault));
        } catch (e) {
            if (e instanceof TypeError) {
                Notification.requestPermission((permission: NotificationPermission) => NotificationManager.handleNotificationPermissionRequest(permission, requesPermissionOnDefault));
            } else {
                throw e;
            }
        }
    }

    //permission = "default" | "denied" | "granted"
    private static handleNotificationPermissionRequest(permission: NotificationPermission, requesPermissionOnDefault: boolean): void {
        if (permission === "granted") {
            console.log("[App] Notification permission request was granted");
            //If authenticated and permission granted configure push subscription
            NotificationManager.createPushSubscription().then(() => {
                console.log("[App] Successfully created PushSubscription");
            });
            return;
        } else if (permission !== "denied") {
            console.log("[App] Notification permission request was dismissed");
            if (!requesPermissionOnDefault) return;
            NotificationManager.requestPermission(false);
            return;
        }
        console.log("[App] Notification permission was denied!");
    }

    static requestNotificationPermission() {
        if ("Notification" in window) {
            /* Notification is available */
            NotificationManager.requestPermission();
        } else {
            console.log("[App] Notification IS NOT available!");
        }
    }
}